/**
 * persisted-crud (localStorage) と互換の CRUD を Firestore 上に提供する (§4.3)。
 *
 * 現行 v2 エディタは property を直接 mutation する (v-model / touch) ため、
 * 変更検知は items の deep watch で行う — ただし §4.1 の禁止事項どおり
 * **watch から Firestore へ直接書かない**。watch は「serialize 済み文字列が前回と
 * 違う id」を dirty にマークするだけで、書込は save-pipeline の debounce 後。
 *
 * 書込内容は diffTopLevel の変更フィールドのみ + メタ (updatedAt=serverTimestamp,
 * updatedBy=email)。top-level optional の消滅は deleteField (§2.5)。
 *
 * 恒久エラー (permission-denied 等) の隔離は save-pipeline 側の quarantine() に
 * 委譲する — 隔離状態・タイマー解除・state='error' の導出は save-pipeline が
 * 一元管理し、ここでは呼ぶだけにする (doc 単位ロック等の並行性は save-pipeline の
 * 責務であり、ここで Set を自前管理すると二重管理になり齟齬の元になる)。
 */
import { ref, watch, type Ref } from 'vue';
import { getFirebase } from '@/lib/firebase/init';
import {
  serializeDoc,
  deserializeDoc,
  type CollectionKind,
} from '@/lib/firebase/serializer';
import { diffTopLevel } from '@/lib/firebase/diff';
import {
  createSavePipeline,
  type SaveState,
} from '@/lib/firebase/save-pipeline';
import { createResendQueue } from '@/lib/firebase/resend-queue';

export interface FirestoreCrud<T extends { id: string; updatedAt: string }> {
  items: Ref<T[]>;
  ready: Ref<boolean>;
  saveState: Ref<SaveState>;
  get(id: string): T | undefined;
  insert(item: T): void;
  update(id: string, patch: Partial<Omit<T, 'id' | 'createdAt'>>): void;
  remove(id: string): void;
  flushAll(): Promise<void>;
  dispose(): void;
  /** deferLoad:true のとき、初回ロードを明示的に開始する (未指定/false なら生成時に自動開始済み)。 */
  startLoad(): void;
}

export function createFirestoreCrud<T extends { id: string; updatedAt: string }>(opts: {
  kind: CollectionKind;
  email: string;
  /**
   * Google アカウントの表示名 (§7 監査フィールド updatedByName/createdByName の元)。
   * firestore.rules の auditOk() は name クレーム欠落ユーザーで updatedByName も
   * 欠落していることを要求する — undefined ではなく必ず null を送る (get(key, null)
   * ベースの比較のため、フィールド省略と明示的な null 送信は同義)。
   */
  displayName?: string | null;
  normalize?: (item: T) => T;
  /** true の場合、生成時に自動ロードせず startLoad() 呼び出しまで待つ (呼び出し側でシード注入等を先に済ませたい場合に使う)。 */
  deferLoad?: boolean;
}): FirestoreCrud<T> {
  const items = ref([]) as Ref<T[]>;
  const ready = ref(false);
  /** 前回同期 (またはキュー投入) 時点のシリアライズ済みスナップショット。 */
  const synced = new Map<string, Record<string, unknown>>();
  /** insert() が発行した setDoc の完了待ち Promise。完了/失敗で自身を削除する。 */
  const creating = new Map<string, Promise<void>>();
  const queue = createResendQueue(opts.email);

  async function flushDoc(docPath: string): Promise<void> {
    const id = docPath.split('/')[1]!;
    // insert 直後 (setDoc 未完了) の doc に updateDoc を投げると not-found になり得るため、
    // 作成完了 (成功/失敗いずれも) を待ってから続行する。
    const c = creating.get(id);
    if (c) await c.catch(() => {});
    const item = items.value.find((x) => x.id === id);
    if (!item) {
      queue.ack(docPath); // remove() 側でも ack するが、経路の防御的二重化
      return; // 既に削除済み
    }
    const next = serializeDoc(opts.kind, item as Record<string, unknown>);
    const prev = synced.get(id) ?? {};
    const { changed, deleted } = diffTopLevel(prev, next);
    if (Object.keys(changed).length === 0 && deleted.length === 0) {
      queue.ack(docPath);
      return;
    }
    // 送信前に再送キューへ (タブ破棄の補償 §4.4)
    queue.enqueue(docPath, next, (prev.updatedAt as string) ?? null);
    const { db } = await getFirebase();
    const { doc, updateDoc, deleteField, serverTimestamp } = await import(
      'firebase/firestore'
    );
    const payload: Record<string, unknown> = {
      ...changed,
      updatedAt: serverTimestamp(),
      updatedBy: opts.email,
      updatedByName: opts.displayName ?? null,
    };
    for (const k of deleted) payload[k] = deleteField();
    try {
      await updateDoc(doc(db, opts.kind, id), payload);
    } catch (e) {
      const code = (e as { code?: string }).code;
      if (code === 'permission-denied' || code === 'not-found' || code === 'invalid-argument') {
        // 恒久失敗 — 無限リトライしない (§4.4)。save-pipeline に隔離を委譲する。
        // insert() の setDoc.catch (下記, function insert 内) も同じ「恒久失敗は quarantine」の思想。
        // ここは save-pipeline の debounce/backoff ループ上にあるため一時失敗と恒久失敗を
        // エラーコードで切り分けられるが、insert はそのループに乗らないため全失敗を quarantine する。
        pipeline.quarantine(docPath);
        return;
      }
      throw e; // 一時失敗 → save-pipeline のバックオフへ
    }
    synced.set(id, next);
    queue.ack(docPath);
  }

  const pipeline = createSavePipeline({ flushDoc });
  const uninstall = pipeline.installGlobalTriggers();

  async function load(): Promise<void> {
    const { db } = await getFirebase();
    const { collection, getDocs } = await import('firebase/firestore');
    const snap = await getDocs(collection(db, opts.kind));
    const list: T[] = [];
    for (const d of snap.docs) {
      // doc 単位で隔離 (§4.3) — 1件の壊れたデータで全読込を落とさない
      try {
        let item = deserializeDoc<T>(opts.kind, d.id, d.data());
        if (opts.normalize) item = opts.normalize(item);
        list.push(item);
        synced.set(d.id, serializeDoc(opts.kind, item as Record<string, unknown>));
      } catch (e) {
        console.warn(`[firestore-crud] ${opts.kind}/${d.id} の読込に失敗`, e);
      }
    }
    items.value = list;
    ready.value = true;
  }
  if (!opts.deferLoad) void load();

  function startLoad(): void {
    void load();
  }

  // deep watch → dirty マークのみ (§4.1 直結禁止)
  const stopWatch = watch(
    items,
    (list) => {
      if (!ready.value) return;
      for (const item of list) {
        const next = serializeDoc(opts.kind, item as Record<string, unknown>);
        const prev = synced.get(item.id);
        // insert() は push と同時に synced へ登録するため、通常ここは load/insert 済み doc のみを
        // 拾う。prev 欠落は理論上到達しない防御的フォールバック (undefined 比較を避けるだけ)。
        if (!prev) continue;
        if (JSON.stringify(prev) !== JSON.stringify(next)) {
          pipeline.markDirty(`${opts.kind}/${item.id}`);
        }
      }
    },
    { deep: true },
  );

  function get(id: string): T | undefined {
    return items.value.find((x) => x.id === id);
  }

  function insert(item: T): void {
    items.value.push(item);
    const data = serializeDoc(opts.kind, item as Record<string, unknown>);
    // push と同時に登録する: setDoc 完了を待たず、以後の編集を watch が diff で拾えるようにする
    // (旧実装は setDoc 完了後にしか synced を登録せず、それまでの編集が dirty 化されなかった)。
    synced.set(item.id, data);
    const docPath = `${opts.kind}/${item.id}`;
    const promise = (async () => {
      const { db } = await getFirebase();
      const { doc, setDoc, serverTimestamp } = await import('firebase/firestore');
      await setDoc(doc(db, opts.kind, item.id), {
        ...data,
        createdBy: opts.email,
        createdByName: opts.displayName ?? null,
        updatedBy: opts.email,
        updatedByName: opts.displayName ?? null,
        updatedAt: serverTimestamp(),
      });
    })()
      .catch((e: unknown) => {
        // flushDoc (上記の catch ブロック) と同じ思想: ルール拒否等の恒久失敗は無限リトライせず
        // save-pipeline に隔離 (quarantine) を委譲し、state='error' でユーザーに見せる
        // (SyncIndicator が「保存できません — 再読み込み」を表示する)。
        // 相違点: flushDoc は save-pipeline の debounce/backoff ループに乗るため一時失敗
        // (throw して runFlush の catch へ) と恒久失敗 (quarantine) をエラーコードで
        // 切り分けられるが、insert の setDoc は一度きりの floating async でそのループに
        // 乗らない (作成のリトライ機構が無い) ため、コード種別を問わず失敗したら即 quarantine
        // する。ここで catch しないと unhandled rejection になり (実際に test:rules を
        // クラッシュさせた)、かつ saveState が 'idle' のままエラーが不可視化し、新規作成物件が
        // サイレントに失われる。items からのロールバックはしない (§4.4: ローカルの編集内容は
        // 失わない — dirty のまま画面に残し、ユーザーは error 表示を見て再読み込みする)。
        console.warn(`[firestore-crud] ${docPath} の insert(setDoc) に失敗`, e);
        pipeline.quarantine(docPath);
      })
      .finally(() => {
        if (creating.get(item.id) === promise) creating.delete(item.id);
      });
    creating.set(item.id, promise);
  }

  function update(id: string, patch: Partial<Omit<T, 'id' | 'createdAt'>>): void {
    const idx = items.value.findIndex((x) => x.id === id);
    if (idx < 0) return;
    items.value[idx] = {
      ...items.value[idx]!,
      ...patch,
      updatedAt: new Date().toISOString(), // 楽観値。サーバ確定値はリロードで反映
    };
    // deep watch が dirty マークする
  }

  function remove(id: string): void {
    const idx = items.value.findIndex((x) => x.id === id);
    if (idx >= 0) items.value.splice(idx, 1);
    synced.delete(id);
    queue.ack(`${opts.kind}/${id}`); // 削除済み doc の再送キューエントリを残骸化させない
    void (async () => {
      const { db } = await getFirebase();
      const { doc, deleteDoc } = await import('firebase/firestore');
      await deleteDoc(doc(db, opts.kind, id));
    })();
  }

  function dispose(): void {
    stopWatch();
    uninstall();
    pipeline.dispose();
  }

  return {
    items,
    ready,
    saveState: pipeline.state,
    get,
    insert,
    update,
    remove,
    flushAll: () => pipeline.flushAll(),
    dispose,
    startLoad,
  };
}
